/**
 * Community Reels Marquee — Custom Element
 *
 * No dependencies, no globals. Registered as <community-reels>.
 * Uses AbortController for cleanup, IntersectionObserver for
 * performance, native <dialog> for the modal.
 */
class CommunityReels extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController();
    const { signal } = this._ac;

    // --- Cache DOM refs ---
    this.section = this.closest('.community-reels');
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.groupA = this.querySelector('[data-group-a]');
    this.groupB = this.querySelector('[data-group-b]');
    this.groupC = this.querySelector('[data-group-c]');
    this.dialog = this.section.querySelector('[data-modal]');
    this.cards = [
      ...this.section.querySelectorAll('[data-reel-card]'),
    ];
    this.videos = [
      ...this.section.querySelectorAll('[data-reel-video]'),
    ];

    // --- Read config from data attributes ---
    this.marqueeEnabled = this.dataset.marquee === 'true';
    this.speed = Number(this.dataset.speed) || 40;
    this.direction = this.dataset.direction || 'left';
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.autoplayInline = this.dataset.autoplay === 'true';
    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );

    // --- State ---
    this._modalOpen = false;
    this._lastFocused = null;
    this._pausedByUser = false; // set on manual scroll/drag
    this._designMode = !!(window.Shopify && Shopify.designMode);

    // --- Init ---
    this._setupPauseTriggers(signal);
    this._setupModal(signal);
    this._setupDragScroll(signal);
    this._initMarquee(); // builds the loop set, sets up observers, starts autoplay

    // --- Shopify Theme Editor events ---
    this._onSectionLoad = () => this._handleSectionLoad();
    this._onSectionUnload = () => this._handleSectionUnload();
    document.addEventListener(
      'shopify:section:load',
      this._onSectionLoad,
    );
    document.addEventListener(
      'shopify:section:unload',
      this._onSectionUnload,
    );

    // Theme resize event (fired by theme.dev.js) — rebuild the loop
    // set since the "at least one viewport wide" copy count may change.
    this._onResize = this._debounce(() => {
      this._buildLoopSet();
      if (this.marqueeEnabled && !this.reducedMotion.matches) {
        this._startMarquee();
      }
    }, 150);
    window.addEventListener('resize', this._onResize);
    document.addEventListener(
      'theme:resize:width',
      this._onResize,
    );
  }

  disconnectedCallback() {
    this._ac.abort();

    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._sectionIO) this._sectionIO.disconnect();
    if (this._cardIO) this._cardIO.disconnect();

    document.removeEventListener(
      'shopify:section:load',
      this._onSectionLoad,
    );
    document.removeEventListener(
      'shopify:section:unload',
      this._onSectionUnload,
    );
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener(
      'theme:resize:width',
      this._onResize,
    );

    // Clean up modal state
    if (this._modalOpen) {
      this._closeModal();
    }
  }

  /* ==================================================================
     Marquee — driven entirely through a CSS transform on the track.

     Auto-scroll (a rAF loop) and manual drag (pointer events, unifying
     mouse and touch) both write the exact same `_offset` number and
     apply it as `translateX(-offset)`. One source of truth, no
     fighting between mechanisms.

     Native scrollLeft was tried first for this — it gives infinite
     drag "for free" — but continuous rAF-driven scrollLeft writes
     turned out to be unreliable in Safari specifically (auto-scroll
     never visibly moved, most likely the theme's global
     `html { scroll-behavior: smooth }` leaking into this nested
     scroll container and fighting each write with a competing
     smooth-scroll animation). Transform is the one technique already
     proven to work across every browser tested here, so it drives
     both auto-scroll and drag; only reduced-motion mode falls back to
     a real native-scroll strip (see the CSS media query).

     The one rule that makes the loop seamless regardless of
     mechanism: set A and set B must be pixel-identical in width. The
     original implementation only ever grew set B (cloning it
     repeatedly) while set A stayed at its original card count — the
     two sets drifted apart, and nothing could loop cleanly on top of
     that. Fix the width symmetry first; everything else follows.
     ================================================================== */

  _initMarquee() {
    // Retry until the viewport has real layout width (can be 0 for a
    // frame or two right after insertion, especially in Safari).
    let attempts = 0;
    const tryInit = () => {
      if (this.viewport.clientWidth > 0 && this.groupA?.children.length > 0) {
        this._buildLoopSet();
        this._setupObservers();
        if (this.marqueeEnabled && !this.reducedMotion.matches) {
          this._startMarquee();
        }
        if (
          this.autoplayInline &&
          !this.reducedMotion.matches &&
          !this._designMode
        ) {
          this._playVisibleVideos();
        }
      } else if (attempts < 20) {
        attempts++;
        requestAnimationFrame(tryInit);
      }
    };
    requestAnimationFrame(tryInit);
  }

  /**
   * Rebuilds group A (repeating the real cards enough times to be at
   * least one viewport wide, so there's never a visible gap) and then
   * makes groups B and C exact clones of the finalized group A.
   *
   * Three identical bands, not two: with only two, native scrollLeft
   * has a hard edge one set-width away in either direction — an
   * auto-scroll wrap or a fast manual drag can reach it before we get
   * a chance to correct, producing exactly the stutter/dead-end this
   * was built to fix. Starting centered in the middle band gives a
   * full set-width of headroom on both sides at all times; edge
   * correction (see _keepWithinBand) then has room to silently jump
   * the position by one set-width — invisible, since the bands are
   * pixel-identical — well before scrollLeft ever hits a true edge.
   *
   * Safe to call again later (resize, Theme Editor re-render): it
   * always starts from the original, pristine card set cached on
   * first run, never from a previous run's already-multiplied output.
   */
  _buildLoopSet() {
    if (!this.groupA || !this.groupB || !this.groupC) return;

    if (!this._sourceCards) {
      this._sourceCards = [...this.groupA.children];
    }
    if (this._sourceCards.length === 0) return;

    this.groupA.innerHTML = '';
    this._sourceCards.forEach((card) =>
      this.groupA.appendChild(card.cloneNode(true)),
    );

    const minWidth = this.viewport.clientWidth;
    let guard = 0;
    while (this.groupA.scrollWidth < minWidth && guard < 20) {
      this._sourceCards.forEach((card) =>
        this.groupA.appendChild(card.cloneNode(true)),
      );
      guard++;
    }

    const cloneGroupFromA = (group) => {
      group.innerHTML = '';
      [...this.groupA.children].forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone
          .querySelectorAll('a, button, [tabindex]')
          .forEach((el) => el.setAttribute('tabindex', '-1'));
        group.appendChild(clone);
      });
    };
    cloneGroupFromA(this.groupB);
    cloneGroupFromA(this.groupC);

    const hadWidth = !!this._setWidth;
    const prevOffsetInBand = hadWidth
      ? ((this._offset % this._setWidth) + this._setWidth) % this._setWidth
      : 0;

    this._setWidth = this.groupA.scrollWidth;
    this.track.style.width = `${this._setWidth * 3}px`;

    // First build: start centered in the middle band. Rebuild
    // (resize, Theme Editor reload): keep the same relative position
    // instead of snapping back to the start.
    this._offset = this._setWidth + prevOffsetInBand;
    this.track.style.transform = `translateX(${-this._offset}px)`;

    // DOM was rebuilt — re-cache and re-observe.
    this.cards = [...this.section.querySelectorAll('[data-reel-card]')];
    this.videos = [...this.section.querySelectorAll('[data-reel-video]')];
    this._reobserveCards();
  }

  /**
   * Called after every offset change (auto-scroll tick, manual drag).
   * If the position has drifted within one card-width of a true track
   * edge, silently jump it back by exactly one set-width —
   * imperceptible since the three bands are pixel-identical —
   * restoring headroom before it's actually needed. Returns the
   * corrected value.
   */
  _keepWithinBand(x) {
    if (!this._setWidth) return x;
    const margin = this._setWidth * 0.1;
    if (x < margin) return x + this._setWidth;
    if (x > this._setWidth * 2 - margin) return x - this._setWidth;
    return x;
  }

  _startMarquee() {
    if (!this._setWidth || this._rafId) return;
    this._marqueePausedFlag = false;
    this._lastTick = performance.now();
    this._rafId = requestAnimationFrame((t) => this._marqueeTick(t));
  }

  _marqueeTick(now) {
    const running =
      !this._marqueePausedFlag && !this._pausedByUser && !this._modalOpen;

    if (running && this._setWidth) {
      const dt = Math.min((now - this._lastTick) / 1000, 0.1);
      this._lastTick = now;

      const delta = this.speed * dt;
      this._offset += this.direction === 'right' ? -delta : delta;
      this._offset = this._keepWithinBand(this._offset);

      this.track.style.transform = `translateX(${-this._offset}px)`;
    } else {
      this._lastTick = now;
    }

    this._rafId = requestAnimationFrame((t) => this._marqueeTick(t));
  }

  _pauseMarquee() {
    this._marqueePausedFlag = true;
  }

  _resumeMarquee() {
    if (this._modalOpen) return;
    if (this._pausedByUser) return;
    if (!this.marqueeEnabled) return;
    this._marqueePausedFlag = false;
    this._lastTick = performance.now();
  }

  /* ==================================================================
     Observers
     ================================================================== */

  _setupObservers() {
    // --- Section-level: pause everything when out of viewport ---
    this._sectionIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._resumeMarquee();
            if (
              this.autoplayInline &&
              !this.reducedMotion.matches &&
              !this._designMode
            ) {
              this._playVisibleVideos();
            }
          } else {
            this._pauseMarquee();
            this._pauseAllVideos();
          }
        });
      },
      { threshold: 0 },
    );
    this._sectionIO.observe(this.section);

    // --- Per-card: pause videos outside viewport ---
    this._cardIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector(
            '[data-reel-video]',
          );
          if (!video) return;
          if (entry.isIntersecting) {
            if (
              this.autoplayInline &&
              !this.reducedMotion.matches &&
              !this._designMode
            ) {
              video.play().catch(() => {}); // autoplay may be blocked
            }
          } else {
            video.pause();
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '150px', // pre-warm cards about to enter
      },
    );

    this._reobserveCards();
  }

  /**
   * Re-targets the card IntersectionObserver after _buildLoopSet()
   * replaces the card elements (initial build, resize, section reload).
   */
  _reobserveCards() {
    if (!this._cardIO) return;
    this._cardIO.disconnect();
    this.cards.forEach((card) => this._cardIO.observe(card));
  }

  _playVisibleVideos() {
    this.videos.forEach((video) => {
      video.muted = true;
      video.play().catch(() => {});
    });
  }

  _pauseAllVideos() {
    this.videos.forEach((video) => {
      video.pause();
    });
  }

  /* ==================================================================
     Pause triggers
     ================================================================== */

  _setupPauseTriggers(signal) {
    const el = this.viewport;

    // Hover
    el.addEventListener(
      'mouseenter',
      () => {
        if (this.pauseOnHover) this._pauseMarquee();
      },
      { signal },
    );
    el.addEventListener(
      'mouseleave',
      () => {
        if (this.pauseOnHover) this._resumeMarquee();
      },
      { signal },
    );

    // Keyboard focus within section. Skipped once when focus is being
    // returned programmatically after the modal closes — otherwise
    // that focus() call immediately re-pauses the marquee we just
    // resumed, and nothing ever moves focus away again to undo it.
    this.section.addEventListener(
      'focusin',
      () => {
        if (this._restoringFocus) {
          this._restoringFocus = false;
          return;
        }
        this._pauseMarquee();
      },
      { signal },
    );
    this.section.addEventListener(
      'focusout',
      (e) => {
        if (!this.section.contains(e.relatedTarget)) {
          this._resumeMarquee();
        }
      },
      { signal },
    );

    // Visibility: pause when tab hidden
    this._onVisibility = () => {
      if (document.hidden) {
        this._pauseMarquee();
        this._pauseAllVideos();
      } else {
        this._resumeMarquee();
        if (
          this.autoplayInline &&
          !this.reducedMotion.matches &&
          !this._designMode
        ) {
          this._playVisibleVideos();
        }
      }
    };
    document.addEventListener(
      'visibilitychange',
      this._onVisibility,
      { signal },
    );

    // Reduced motion change
    this._onReducedMotionChange = (e) => {
      if (e.matches) {
        if (this._rafId) {
          cancelAnimationFrame(this._rafId);
          this._rafId = null;
        }
        this._pauseAllVideos();
      } else if (this.marqueeEnabled) {
        this._startMarquee();
      }
      // Viewport scroll-snap for reduced motion is handled
      // declaratively by the @media (prefers-reduced-motion) CSS rule.
    };
    this.reducedMotion.addEventListener(
      'change',
      this._onReducedMotionChange,
    );
  }

  /* ==================================================================
     Modal
     ================================================================== */

  _setupModal(signal) {
    // Click delegation on section: open modal on card click
    this.section.addEventListener(
      'click',
      (e) => {
        // Product links: never trigger modal
        if (e.target.closest('[data-reel-product]')) return;
        // Close button within modal
        if (e.target.closest('[data-modal-close]')) {
          this._closeModal();
          return;
        }
        // Backdrop click
        if (e.target === this.dialog) {
          this._closeModal();
          return;
        }

        const card = e.target.closest('[data-reel-card]');
        if (!card) return;
        // Don't open from empty placeholder cards
        if (card.classList.contains('community-reels__card--empty'))
          return;
        this._openModal(card);
      },
      { signal },
    );

    // ESC key
    this.dialog.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') {
          // Native dialog handles ESC; we just clean up
          this._closeModal();
        }
        // Focus trap
        if (e.key === 'Tab') {
          this._trapFocus(e);
        }
      },
      { signal },
    );

    // Native dialog close event (ESC, or dialog.close())
    this.dialog.addEventListener(
      'close',
      () => this._onDialogClosed(),
      { signal },
    );
  }

  _openModal(card) {
    if (this._modalOpen) return; // guard double-open

    // Modal gets the largest available rendition — the inline card
    // deliberately uses a small one to keep the always-playing
    // marquee cheap, but a user who clicked to view it large should
    // see the best quality Shopify generated for this upload.
    const src = card.dataset.videoSrcModal || card.dataset.videoSrc;
    const type = card.dataset.videoTypeModal || card.dataset.videoType;
    const poster = card.dataset.videoPoster;
    const template = card.querySelector(
      '[data-reel-modal-template]',
    );

    if (!src || !template) return;

    // Clone template content into dialog
    this.dialog.innerHTML = '';
    this.dialog.appendChild(template.content.cloneNode(true));

    // Set up modal video
    const modalVideo =
      this.dialog.querySelector('[data-modal-video]');
    if (modalVideo) {
      const source = document.createElement('source');
      source.src = src;
      source.type = type;
      modalVideo.appendChild(source);
      if (poster) modalVideo.poster = poster;
      modalVideo.currentTime = 0;
    }

    // Pause inline
    const cardVideo = card.querySelector('[data-reel-video]');
    if (cardVideo) cardVideo.pause();

    // Pause marquee
    this._pauseMarquee();
    this.section.classList.add('community-reels--modal-open');
    document.body.style.overflow = 'hidden';

    // Store trigger for focus return
    this._lastFocused =
      card.querySelector('[data-reel-play]') || card;

    this._modalOpen = true;

    // Open dialog (must be called from user gesture for audio)
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      // Safari <15.4 fallback
      this.dialog.setAttribute('open', '');
      // Manual backdrop
      this._addFallbackBackdrop();
    }

    // Play with sound
    if (modalVideo) {
      modalVideo.play().catch(() => {});
    }

    // Focus close button
    const closeBtn =
      this.dialog.querySelector('[data-modal-close]');
    if (closeBtn) closeBtn.focus();
  }

  _closeModal() {
    if (!this._modalOpen) return;
    this.dialog.close();
  }

  _onDialogClosed() {
    if (!this._modalOpen) return;

    // Clean up modal video
    const modalVideo =
      this.dialog.querySelector('[data-modal-video]');
    if (modalVideo) {
      modalVideo.pause();
      modalVideo.removeAttribute('src');
      modalVideo.load();
    }

    // Clear dialog
    this.dialog.innerHTML = '';

    // Restore body scroll
    document.body.style.overflow = '';
    this.section.classList.remove('community-reels--modal-open');

    // Remove fallback backdrop
    this._removeFallbackBackdrop();

    this._modalOpen = false;

    // Resume marquee
    this._resumeMarquee();

    // Return focus
    if (this._lastFocused) {
      this._restoringFocus = true;
      this._lastFocused.focus();
      this._lastFocused = null;
    }
  }

  _trapFocus(e) {
    const focusable = [
      ...this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (
      !e.shiftKey &&
      document.activeElement === last
    ) {
      e.preventDefault();
      first.focus();
    }
  }

  /* Safari <15.4 dialog polyfill helpers */
  _addFallbackBackdrop() {
    if (this._fallbackBackdrop) return;
    const backdrop = document.createElement('div');
    backdrop.className = 'community-reels__modal-backdrop';
    backdrop.setAttribute('data-modal-backdrop', '');
    backdrop.addEventListener('click', () => this._closeModal());
    this.dialog.parentNode.insertBefore(
      backdrop,
      this.dialog,
    );
    this._fallbackBackdrop = backdrop;
  }

  _removeFallbackBackdrop() {
    if (this._fallbackBackdrop) {
      this._fallbackBackdrop.remove();
      this._fallbackBackdrop = null;
    }
  }

  /* ==================================================================
     Drag scroll (reduced motion / marquee off)
     ================================================================== */

  /**
   * Drag/swipe via Pointer Events (unifies mouse and touch — no
   * separate native-scroll code path needed, which sidesteps the
   * Safari scrollLeft issues entirely: everything here writes the
   * same transform-driven offset the auto-scroll loop uses). A
   * movement threshold keeps a plain click from ever being treated
   * as a drag, so the card's click handler always gets an untouched
   * click straight through.
   */
  _setupDragScroll(signal) {
    const DRAG_THRESHOLD = 6;
    let pending = false;
    let dragging = false;
    let startX = 0;
    let baseOffset = 0;
    let resumeTimer = null;

    const onPointerDown = (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      pending = true;
      dragging = false;
      startX = e.clientX;
      baseOffset = this._offset;
    };

    const onPointerMove = (e) => {
      if (!pending) return;
      const dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        this._pausedByUser = true;
        clearTimeout(resumeTimer);
        this.viewport.style.cursor = 'grabbing';
        this.viewport.setPointerCapture?.(e.pointerId);
      }
      this._offset = this._keepWithinBand(baseOffset - dx);
      this.track.style.transform = `translateX(${-this._offset}px)`;
    };

    const onPointerUp = () => {
      pending = false;
      if (!dragging) return; // was just a click — leave it alone
      dragging = false;
      this.viewport.style.cursor = '';
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        this._pausedByUser = false;
      }, 1200);
    };

    this.viewport.addEventListener('pointerdown', onPointerDown, { signal });
    this.viewport.addEventListener('pointermove', onPointerMove, { signal });
    this.viewport.addEventListener('pointerup', onPointerUp, { signal });
    this.viewport.addEventListener('pointercancel', onPointerUp, { signal });
  }

  /* ==================================================================
     Shopify Theme Editor
     ================================================================== */

  _handleSectionLoad() {
    requestAnimationFrame(() => {
      this._buildLoopSet();
      if (this.marqueeEnabled && !this.reducedMotion.matches) {
        this._startMarquee();
      }
    });
  }

  _handleSectionUnload() {
    // Clean up before the editor removes this section
    this.disconnectedCallback();
  }

  /* ==================================================================
     Utilities
     ================================================================== */

  _debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

// Register (only once)
if (!customElements.get('community-reels')) {
  customElements.define('community-reels', CommunityReels);
}
